// Lib
import { mapInlineStyleToMark } from '@tiptap/draftjs-to-tiptap';
import { TiptapMarkType } from '../../../tiptapTypes';
import type { TiptapContentMark } from '../../../tiptapTypes';

// Types
import { MapStyleToMarkFn, StyleToMarkContext } from './draftToTiptapConverterTypes';

const DRAFT_TEXT_COLOR_PREFIX = 'TEXT-';
const DRAFT_BACKGROUND_COLOR_PREFIX = 'BG-';

const convertColoredTextToTiptapMark = (style: string): TiptapContentMark => {
    return {
        type: TiptapMarkType.textStyle,
        attrs: {
            color: style.replace(DRAFT_TEXT_COLOR_PREFIX, ''),
        },
    };
};

const convertHighlightedTextToTiptapMark = (style: string): TiptapContentMark => {
    return {
        type: TiptapMarkType.highlight,
        attrs: {
            color: style.replace(DRAFT_BACKGROUND_COLOR_PREFIX, '').replace('-', ''),
        },
    };
};

const createStyleToMarkContext = (style: string): StyleToMarkContext =>
    ({
        range: { style },
    }) as StyleToMarkContext;

/**
 * Converts a Milanote inline style string into the equivalent Tiptap mark when one exists.
 */
export const convertInlineStyleToTiptapMark = (style: string): TiptapContentMark | undefined => {
    if (style.startsWith(DRAFT_TEXT_COLOR_PREFIX)) {
        return convertColoredTextToTiptapMark(style);
    }

    if (style.startsWith(DRAFT_BACKGROUND_COLOR_PREFIX)) {
        return convertHighlightedTextToTiptapMark(style);
    }

    // Otherwise it's bold/italic/etc, which are all just in draftjs format,
    // so we can use the conversion function directly.
    return mapInlineStyleToMark(createStyleToMarkContext(style)) ?? undefined;
};

/**
 * Converts Draft.js inline style to Tiptap marks.
 * Adds custom Milanote-specific handling for text and background color, otherwise
 * delegates to the default draftjs-to-tiptap implementation.
 */
export const convertDraftInlineStyleToTiptapMark: MapStyleToMarkFn = (context) => {
    const { range } = context;

    const inlineStyleMark = convertInlineStyleToTiptapMark(range.style);
    if (inlineStyleMark) return inlineStyleMark;

    return mapInlineStyleToMark(context);
};

export default convertDraftInlineStyleToTiptapMark;
